import { useCallback, useEffect, useRef } from 'react';
import type {
  KlarnaAuthorizeResponse,
  KlarnaLoadResponse,
} from '@/hooks/payments/klarna-types';

const KLARNA_SDK_URL = 'https://x.klarnacdn.net/kp/lib/v1/api.js';

const loadKlarnaScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.Klarna?.Payments) {
      resolve();
      return;
    }
    window.klarnaAsyncCallback = () => resolve();
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${KLARNA_SDK_URL}"]`,
    );
    if (existing) {
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.onerror = () => reject(new Error('Klarna SDK failed to load'));
    script.src = KLARNA_SDK_URL;
    document.body.appendChild(script);
  });

export const useKlarnaSdk = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      containerRef.current?.remove();
      containerRef.current = null;
    };
  }, []);

  const authorize = useCallback(
    async ({
      category,
      clientToken,
    }: {
      category: string;
      clientToken: string;
    }): Promise<KlarnaAuthorizeResponse> => {
      await loadKlarnaScript();
      const klarna = window.Klarna;
      if (!klarna) {
        throw new Error('Klarna SDK not available after load');
      }

      klarna.Payments.init({ client_token: clientToken });

      if (!containerRef.current) {
        const container = document.createElement('div');
        container.style.display = 'none';
        document.body.appendChild(container);
        containerRef.current = container;
      }

      await new Promise<KlarnaLoadResponse>((resolve, reject) => {
        klarna.Payments.load(
          {
            container: containerRef.current!,
            payment_method_category: category,
          },
          (response) => {
            if (!response.show_form || response.error) {
              reject(new Error('Klarna load: not offered'));
              return;
            }
            resolve(response);
          },
        );
      });

      return new Promise<KlarnaAuthorizeResponse>((resolve) => {
        klarna.Payments.authorize(
          { payment_method_category: category },
          (response) => {
            resolve(response);
          },
        );
      });
    },
    [],
  );

  return { authorize };
};
